import React from 'react';
import * as nodeEmoji from 'node-emoji';

/**
 * Payload típico do emoji-mart (@emoji-mart/react) no onEmojiSelect.
 * Garante que sempre inserimos o caractere Unicode, nunca shortcode/id (ex.: "rostinho").
 */
export type EmojiMartEmoji = {
  native?: string;
  unified?: string;
  id?: string;
  shortcodes?: string | string[];
  skins?: Array<{ native: string }>;
};

/**
 * Converte o objeto do emoji-mart em um único caractere (ou sequência) Unicode para inserir no texto.
 * Ordem: native → skins[0].native → unified (hex) → shortcodes via node-emoji.
 * Nunca retorna id nem shortcode em texto (evita "rostinho" ou ":smile:" no chat).
 */
export function emojiMartToUnicode(emoji: EmojiMartEmoji | null | undefined): string {
  if (!emoji) return '';

  // 1) Preferir sempre o caractere nativo (o que o WhatsApp e o protocolo usam)
  let char = emoji.native ?? emoji.skins?.[0]?.native ?? '';

  // 2) Se não veio native (ex.: locale pt em alguns builds), usar unified
  if (!char && emoji.unified) {
    try {
      const codePoints = emoji.unified.split('-').map((s: string) => parseInt(s, 16));
      char = String.fromCodePoint(...codePoints);
    } catch (_) {}
  }

  // 3) Último recurso: shortcode do picker -> node-emoji para obter o Unicode (nunca inserir o texto "rostinho")
  if (!char && (emoji.shortcodes || emoji.id)) {
    const raw = Array.isArray(emoji.shortcodes) ? emoji.shortcodes[0] : (emoji.shortcodes ?? emoji.id ?? '');
    const shortcode = typeof raw === 'string' ? raw : '';
    if (shortcode) {
      const withColons = shortcode.includes(':') ? shortcode : `:${shortcode}:`;
      const resolved = nodeEmoji.emojify(withColons, { fallback: '' });
      if (resolved !== withColons) char = resolved;
    }
  }

  return char;
}


export function normalizeWhatsAppIncomingText(text: string): string {
  if (!text) return text;
  return (
    text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\u2028/g, '\n')
      .replace(/\u2029/g, '\n')
      .replace(/\u0085/g, '\n')
      .replace(/\f/g, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/&lt;br\s*\/?&gt;/gi, '\n')
  );
}


/**
 * Resolve um número de telefone (apenas dígitos) para um nome amigável.
 * Retornar undefined deixa o fallback formatar como telefone (BR/internacional).
 */
export type MentionResolver = (digits: string) => string | undefined;

/** Formata um número (apenas dígitos) em pt-BR; cai em padrão internacional. */
export function formatPhoneBR(digits: string): string {
  const d = digits.replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('55')) {
    return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  }
  if (d.length === 12 && d.startsWith('55')) {
    return `+55 (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  }
  if (d.length === 11) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  }
  return d;
}

/** WhatsApp: @ seguido de 6–24 dígitos (telefone ou LID interno em grupos). */
const MENTION_REGEX = /@(\d{6,24})/g;

function processMentions(
  text: string,
  resolver: MentionResolver | undefined,
  baseKey: string
): React.ReactNode[] {
  if (!text) return [];
  if (!resolver && !MENTION_REGEX.test(text)) {
    MENTION_REGEX.lastIndex = 0;
    return [text];
  }
  MENTION_REGEX.lastIndex = 0;
  const out: React.ReactNode[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    if (match.index > lastIdx) {
      out.push(<React.Fragment key={`${baseKey}-t${i++}`}>{text.slice(lastIdx, match.index)}</React.Fragment>);
    }
    const digits = match[1];
    const resolved = resolver?.(digits);
    const display = resolved && resolved.trim() ? resolved.trim() : formatPhoneBR(digits);
    out.push(
      <span key={`${baseKey}-m${i++}`} className="whatsapp-mention">@{display}</span>
    );
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) {
    out.push(<React.Fragment key={`${baseKey}-t${i++}`}>{text.slice(lastIdx)}</React.Fragment>);
  }
  return out;
}

function formatWhatsAppTextSingleLine(
  line: string,
  lineIndex: number,
  resolver?: MentionResolver
): React.ReactNode {
  const parts = line.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~)/g);

  return parts.map((part, index) => {
    const key = `${lineIndex}-${index}`;
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <strong key={key}>{processMentions(part.slice(1, -1), resolver, key)}</strong>;
    }
    if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
      return <em key={key}>{processMentions(part.slice(1, -1), resolver, key)}</em>;
    }
    if (part.startsWith('~') && part.endsWith('~') && part.length > 2) {
      return (
        <span key={key} style={{ textDecoration: 'line-through' }}>
          {processMentions(part.slice(1, -1), resolver, key)}
        </span>
      );
    }
    if (part.startsWith('*') && part.length > 1) {
      return <strong key={key}>{processMentions(part.slice(1), resolver, key)}</strong>;
    }
    if (part.startsWith('_') && part.length > 1) {
      return <em key={key}>{processMentions(part.slice(1), resolver, key)}</em>;
    }
    if (part.startsWith('~') && part.length > 1) {
      return (
        <span key={key} style={{ textDecoration: 'line-through' }}>
          {processMentions(part.slice(1), resolver, key)}
        </span>
      );
    }
    return <React.Fragment key={key}>{processMentions(part, resolver, key)}</React.Fragment>;
  });
}

/**
 * Formata *negrito*, _itálico_, ~rasurado~. Preserva quebras de linha (WhatsApp / \n / &lt;br&gt;).
 * Não interpreta `:3` nem emoticons ASCII como emoji; emoji só se já for Unicode.
 *
 * `mentionResolver` (opcional) converte `@<digits>` no texto para um nome amigável
 * (ex.: "@5511999999999" → "@João Silva"). IDs longos em grupos são LID do WhatsApp.
 * Se não resolver, formata como telefone BR ou mantém dígitos.
 */
export function formatWhatsAppText(text: string, mentionResolver?: MentionResolver): React.ReactNode {
  if (!text) return null;

  const normalized = normalizeWhatsAppIncomingText(text);
  const lines = normalized.split('\n');

  return (
    <span className="whatsapp-text-formatted">
      {lines.map((line, lineIndex) => (
        <React.Fragment key={lineIndex}>
          {lineIndex > 0 ? '\n' : null}
          {formatWhatsAppTextSingleLine(line, lineIndex, mentionResolver)}
        </React.Fragment>
      ))}
    </span>
  );
}
